import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * DashboardContext — persists Dashboard page UI state across navigation AND F5 refresh.
 * Lives above the router so it never unmounts when switching pages.
 *
 * Uses "Direct Consume" pattern (same as LiveTracking/Geofencing) — see
 * /app/memory/REFACTOR_NOTES.md for pattern decision tree.
 *
 * Dashboard has very little persistent UI state because most of its data is
 * server-side (stats, devices, recent events, event stats — all re-fetched on mount).
 * The only meaningful persisted fields are the AI Assistant query + response, so
 * users don't lose what they asked or what the assistant answered when they
 * navigate to another tab and come back.
 */
const DashboardContext = createContext(null);

export const useDashboard = () => {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
};

const STORAGE_KEY = 'db_state';

const loadPersistedState = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

export const DashboardProvider = ({ children }) => {
  const saved = loadPersistedState();

  // ── AI Assistant state ──
  const [aiQuery, setAiQuery] = useState(saved?.aiQuery || '');
  const [aiResponse, setAiResponse] = useState(saved?.aiResponse || '');

  // ── Save to sessionStorage on state changes ──
  useEffect(() => {
    const state = { aiQuery, aiResponse };
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [aiQuery, aiResponse]);

  return (
    <DashboardContext.Provider value={{
      aiQuery, setAiQuery,
      aiResponse, setAiResponse,
    }}>
      {children}
    </DashboardContext.Provider>
  );
};
